
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ScanSearch, Grid, Zap, Clock, TrendingUp, Target, Award, Plane } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const SuperchargedPage = () => {
  const howItWorks = [
    {
      icon: <ScanSearch className="h-12 w-12 text-primary" />,
      title: "Scan & Discover",
      description: "Enter a website URL. Our intelligent scanner crawls the site, identifying optimal landing pages for Google Ads—from individual products to service pages.",
    },
    {
      icon: <Grid className="h-12 w-12 text-primary" />,
      title: "Bulk Configure & Refine",
      description: "Review identified pages, bulk-select them, assign campaign goals, define budgets, and specify target countries and languages.",
    },
    {
      icon: <Zap className="h-12 w-12 text-primary" />,
      title: "Automate & Launch",
      description: "With a single click, our system instantly creates unique, high-quality Google Ads campaigns for every selected page and locale.",
    },
  ];

  const benefits = [
    {
      icon: <Clock className="h-8 w-8 text-primary" />,
      title: "Massive Time Savings",
      description: "Drastically reduce campaign setup time, freeing your team for higher-value strategic work.",
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-primary" />,
      title: "Scalability on Demand",
      description: "Seamlessly manage unlimited product launches and multi-national expansions.",
    },
    {
      icon: <Target className="h-8 w-8 text-primary" />,
      title: "Precision at Scale",
      description: "Every generated campaign is highly relevant to its specific landing page, brand, and locale.",
    },
    {
      icon: <Award className="h-8 w-8 text-primary" />,
      title: "Consistent Quality",
      description: "Leverage our advanced AI for keywords and ad copy, maintaining top-tier ad quality.",
    },
     {
      icon: <Plane className="h-8 w-8 text-primary" />,
      title: "Rapid Market Entry",
      description: "Launch campaigns for new products or geographies in record time to seize market opportunities.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Supercharged Google Ads | Bulk Campaign Creation | Upfixe</title>
        <meta name="description" content="Create dozens of Google Ads campaigns in minutes. Scan websites, bulk configure, and automate campaign launch for multiple landing pages." />
        <meta name="keywords" content="bulk campaign creation, Google Ads automation, website scanning, multi-page campaigns" />
        <meta property="og:title" content="Supercharged Google Ads | Bulk Campaign Creation | Upfixe" />
        <meta property="og:description" content="Create dozens of Google Ads campaigns in minutes. Scan websites, bulk configure, and automate campaign launch for multiple landing pages." />
        <link rel="canonical" href="https://upfixe.com/supercharged" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <section className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <Badge className="px-4 py-2 text-lg bg-amber-500 text-slate-900 border-amber-400 font-bold">
                Coming Soon
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Supercharge Your Agency's Growth</h1>
            <p className="text-xl md:text-2xl text-primary mb-6">Launch Campaigns at Unprecedented Scale</p>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Tired of manual campaign setup for every product, service, country, or language? The Upfixe Supercharged Add-on will let you scan websites, intelligently identify landing pages, and generate hundreds of tailored Google Ads campaigns in minutes.
            </p>
             <div className="relative max-w-4xl mx-auto">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                alt="Dynamic animation of website scanning and campaign generation"
                className="w-full h-auto object-cover rounded-lg shadow-lg"
              />
            </div>
          </section>

          {/* How It Works Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold">How It Works: Scan, Select, Generate.</h2>
              <p className="text-lg text-muted-foreground mt-2 max-w-2xl mx-auto">
                A simple, powerful 3-step process to revolutionize your workflow.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((step) => (
                <Card key={step.title} className="text-center">
                  <CardHeader>
                    <div className="mx-auto bg-primary/10 rounded-full p-4 w-fit mb-4">
                      {step.icon}
                    </div>
                    <CardTitle>{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-20 bg-primary/5 p-8 rounded-lg">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold">Unrivaled Benefits for Agencies & Enterprises</h2>
               <p className="text-lg text-muted-foreground mt-2 max-w-2xl mx-auto">
                Transform your campaign management from a bottleneck to your greatest advantage.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 mt-1">{benefit.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold mb-1">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* CTA Section */}
          <section>
            <Card className="max-w-3xl mx-auto text-center bg-card border-primary/20 p-6">
              <CardHeader>
                <CardTitle className="text-3xl">Get Notified When Supercharged Launches</CardTitle>
                <CardDescription className="text-lg mt-2">
                  The Supercharged Add-on will be available for <span className="font-bold text-primary">$599/month</span>.
                </CardDescription>
                 <p className="text-sm text-muted-foreground">Will require an active Unlimited Plan subscription.</p>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="mailto:hello@upfixe.com?subject=Supercharged Early Access Request&body=Hi, I'm interested in being notified when the Supercharged add-on becomes available.">Get Notified When Available</a>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href="mailto:hello@upfixe.com?subject=Supercharged Early Access Inquiry">Join Early Access List</a>
                </Button>
              </CardContent>
            </Card>
          </section>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SuperchargedPage;
